import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';

import usePaginatedQuery from '@sorare/core/src/hooks/graphql/usePaginatedQuery';
import useInfiniteScroll from '@sorare/core/src/hooks/useInfiniteScroll';
import { useTitleAndDescription } from '@sorare/core/src/hooks/useTitleAndDescription';
import { metadatas } from '@sorare/core/src/lib/seo/football';

import { Matches as DumbMatches } from '@sorare/gameplay/src/components/seo/Matches';
import MatchViewDialog from '@sorare/gameplay/src/components/stats/MatchViewDialog';
import { anyGameToGenericGame } from '@sorare/gameplay/src/lib/anyGame';

import { useLayoutData } from 'pages/leagues/$slug/layout';

import {
  LeagueFutureFixturesQuery,
  LeagueFutureFixturesQueryVariables,
  LeaguePastFixturesQuery,
  LeaguePastFixturesQueryVariables,
} from './__generated__/page.graphql';

const LEAGUE_PAST_FIXTURES_QUERY = gql`
  query LeaguePastFixturesQuery($slug: String!, $cursor: String) {
    football {
      competition(slug: $slug) {
        slug
        pastGames(first: 10, after: $cursor) {
          nodes {
            id
            ...anyGameToGenericGame_anyGameInterface
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${anyGameToGenericGame.fragments.anyGameInterface}
` as TypedDocumentNode<
  LeaguePastFixturesQuery,
  LeaguePastFixturesQueryVariables
>;

const LEAGUE_FUTURE_FIXTURES_QUERY = gql`
  query LeagueFutureFixturesQuery($slug: String!, $cursor: String) {
    football {
      competition(slug: $slug) {
        slug
        futureGames(first: 10, after: $cursor) {
          nodes {
            id
            ...anyGameToGenericGame_anyGameInterface
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
      }
    }
  }
  ${anyGameToGenericGame.fragments.anyGameInterface}
` as TypedDocumentNode<
  LeagueFutureFixturesQuery,
  LeagueFutureFixturesQueryVariables
>;

const Fixtures = () => {
  const { slug, displayName } = useLayoutData();
  const [matchViewGameId, setMatchViewGameId] = useState<string>();

  useTitleAndDescription(
    metadatas.leagueFixtures.title,
    metadatas.leagueFixtures.description,
    {
      league: displayName,
    }
  );

  const {
    data: dataFuture,
    loading: loadingFuture,
    loadMore: loadMoreFuture,
  } = usePaginatedQuery(LEAGUE_FUTURE_FIXTURES_QUERY, {
    variables: { slug: slug || '' },
    connection: 'GameConnection',
    skip: !slug,
  });

  const { InfiniteScrollLoader: InfiniteScrollLoaderFuture } =
    useInfiniteScroll(
      () => {
        loadMoreFuture(false, {
          cursor:
            dataFuture?.football.competition.futureGames.pageInfo.endCursor,
        });
      },
      !!dataFuture?.football.competition.futureGames.pageInfo.hasNextPage,
      loadingFuture
    );

  const {
    data: dataPast,
    loading: loadingPast,
    loadMore: loadMorePast,
  } = usePaginatedQuery(LEAGUE_PAST_FIXTURES_QUERY, {
    variables: { slug: slug || '' },
    connection: 'GameConnection',
    skip: !slug,
  });

  const { InfiniteScrollLoader: InfiniteScrollLoaderPast } = useInfiniteScroll(
    () => {
      setTimeout(() => {
        loadMorePast(false, {
          cursor: dataPast?.football.competition.pastGames.pageInfo.endCursor,
        });
      }, 500);
    },
    !!dataPast?.football.competition.pastGames.pageInfo.hasNextPage,
    loadingPast
  );

  const pastGames = [
    ...(dataPast?.football.competition.pastGames.nodes || []),
  ].reverse();
  const futureGames = dataFuture?.football.competition.futureGames.nodes || [];

  return (
    <>
      <DumbMatches
        displayName={displayName}
        loading={loadingPast || loadingFuture}
        pastGames={pastGames.map(anyGame =>
          anyGameToGenericGame({
            anyGame,
            onClick: id => setMatchViewGameId(id),
          })
        )}
        infiniteScrollLoaderPast={<InfiniteScrollLoaderPast />}
        futureGames={futureGames.map(anyGame =>
          anyGameToGenericGame({ anyGame })
        )}
        infiniteScrollLoaderFuture={<InfiniteScrollLoaderFuture />}
      />
      <MatchViewDialog
        open={!!matchViewGameId}
        onClose={() => setMatchViewGameId(undefined)}
        gameId={matchViewGameId}
      />
    </>
  );
};

export default Fixtures;
